import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { usePost, get_deployment } from '../API';

export type Transaction = {
  action_id: string;
  transaction_id: string | null;
  target_field: string | null;
  amount: number;
  subledger: string | null;
  payment_provider: string;
  rc_status: string;
  transaction_state: string | null;
  transaction_clear_date: string | null;
  uid: string | null;
  entry_type: string;
  card_id: string | null;
  failure_message: string | null;
  trace_id: string | null;
  givecard_recipient_id: string | null;
  attempt: number;
};

type TransactionsContextType = {
  transactions: Transaction[];
  isLoading: boolean;
  error: string | null;
  fetchTransactions: (startDate?: string, endDate?: string) => Promise<void>;
};

const TransactionsContext = createContext<TransactionsContextType | undefined>(undefined);

export const useTransactionsContext = () => {
  const context = useContext(TransactionsContext);
  if (!context) {
    throw new Error('useTransactionsContext must be used within a TransactionsProvider');
  }
  return context;
};

type TransactionsProviderProps = {
  children: ReactNode;
};

export const TransactionsProvider: React.FC<TransactionsProviderProps> = ({ children }) => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const postTransactionDetails = usePost('/api/admin_panel/transaction_details');

  const fetchTransactions = useCallback(async (startDate?: string, endDate?: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await postTransactionDetails({
        program: get_deployment(),
        startDate,
        endDate,
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      setTransactions(response.data?.transactions || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      setTransactions([]);
    } finally {
      setIsLoading(false);
    }
  }, [postTransactionDetails]);

  const value: TransactionsContextType = {
    transactions,
    isLoading,
    error,
    fetchTransactions,
  };

  return (
    <TransactionsContext.Provider value={value}>
      {children}
    </TransactionsContext.Provider>
  );
};
